/**
 * useWorkflowInstanceTimeline - Progresso da esteira (Workflow 2.0)
 *
 * Lê a RPC segura `get_workflow_instance_timeline`, que resolve a instância
 * pelo id OU pela entidade-raiz (ex.: um negócio do CRM) e devolve a etapa
 * atual, as etapas já percorridas e os próximos passos configurados na versão
 * publicada do processo.
 *
 * A chave de cache é `['deal-workflow', entityId]`, invalidada pelas mutações
 * de produtos/negócio e pelo realtime por registro (useWorkflowRecordRealtime).
 *
 * Como a entrada no processo é assíncrona (motor por fila), este hook mantém
 * uma JANELA DE ACOMPANHAMENTO: durante ~60s após uma alteração relevante do
 * registro (evento DOM `workflow:deal-changed`), refaz a leitura a cada 3s e
 * desliga sozinho. Fora da janela, o custo é o mesmo de antes (nenhum polling).
 *
 * @version 1.2.0
 * RELEASE: 2026-09-13 v1.2.0 - Tipagem dos detalhes expansíveis das fases:
 *          configuração, filtros, ações configuradas/executadas e caminhos.
 * RELEASE: 2026-09-01 v1.1.0 - Janela de acompanhamento pós-alteração (refetch
 *          a cada 3s por até 60s) para refletir na aba Resumo, sem F5, a
 *          instância criada/encerrada pelo motor assíncrono.
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (trilha no modal do negócio)
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useWorkflowInstanceTimeline');

/** Nome do evento DOM que abre a janela de acompanhamento após alteração do registro */
export const WORKFLOW_DEAL_CHANGED_EVENT = 'workflow:deal-changed';

/** Intervalo de refetch dentro da janela de acompanhamento */
const JANELA_INTERVALO_MS = 3_000;

/** Duração máxima da janela de acompanhamento */
const JANELA_DURACAO_MS = 60_000;

/** Campo declarado no contrato de parâmetros de uma ação do Workflow. */
export interface WorkflowTrailParamContract {
  key: string;
  label: string;
  type?: string;
  multiple?: boolean;
}

/** Ação configurada ou efetivamente executada dentro de uma fase. */
export interface WorkflowTrailActionDetail {
  codigo: string;
  nome: string;
  parametros?: Record<string, unknown> | null;
  contrato_parametros?: WorkflowTrailParamContract[];
  status?: string | null;
  iniciado_em?: string | null;
  finalizado_em?: string | null;
  erro?: string | null;
}

/** Caminho de saída configurado para uma fase percorrida. */
export interface WorkflowTrailPathDetail {
  rotulo?: string | null;
  destino: string;
  evento_codigo?: string | null;
  evento_nome?: string | null;
  condicoes?: unknown;
  enforcement?: string | null;
  padrao?: boolean;
  regressao?: boolean;
}

/** Informações somente leitura exibidas ao expandir uma fase. */
export interface WorkflowTrailDetails {
  origem_configuracao?: 'historica' | 'vigente';
  resumo_configuracao?: Record<string, unknown> | null;
  filtros_entrada?: unknown;
  condicoes_caminho?: unknown;
  acoes_entrada?: WorkflowTrailActionDetail[];
  acoes_conclusao?: WorkflowTrailActionDetail[];
  acoes_executadas?: WorkflowTrailActionDetail[];
  caminhos_saida?: WorkflowTrailPathDetail[];
}

/** Etapa já percorrida pela instância */
export interface WorkflowTrailStep {
  node_id: string;
  label: string;
  node_type: string;
  status: string;
  ocorrido_em: string | null;
  finalizado_em: string | null;
  atual: boolean;
  /** Posição do bloco no desenho do processo (workflow_nodes.sort_order) */
  ordem_fluxo?: number | null;
  /** Configuração e execução sanitizadas para consulta pelo usuário. */
  detalhes?: WorkflowTrailDetails | null;
}

/** Próximo passo configurado a partir da etapa atual */
export interface WorkflowTrailNext {
  node_id: string;
  label: string;
  node_type: string;
  aresta_label: string | null;
  event_code: string | null;
  event_label: string | null;
  tem_condicao: boolean;
  enforcement: string | null;
  is_default: boolean;
  is_regression: boolean;
  /** Configuração vigente sanitizada da próxima fase. */
  detalhes?: WorkflowTrailDetails | null;
}

/** Retorno completo da RPC */
export interface WorkflowInstanceProgress {
  instance_id: string | null;
  status?: string;
  error_summary?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  // v1.3.0 (RPC): sinaliza que a instância ainda está ancorada em versão antiga
  desatualizada?: boolean;
  // v1.5.0 (RPC): processo em status final (cancelado/concluído/erro/compensado):
  // não existe etapa atual nem próximos passos
  encerrada?: boolean;
  em_revisao?: boolean;
  definicao?: {
    id: string;
    nome: string;
    /** Versão que está de fato valendo (vigente) */
    versao: number | null;
    /** Versão em que a instância permanece ancorada (quando desatualizada) */
    versao_instancia?: number | null;
    entidade_raiz: string;
  } | null;
  etapa_atual?: {
    node_id: string;
    label: string;
    node_type: string;
    entrou_em: string | null;
    pendencias_abertas: number;
    prazo: string | null;
  } | null;
  etapas: WorkflowTrailStep[];
  proximos_passos: WorkflowTrailNext[];
  /**
   * v1.6.0 (RPC): TODOS os processos do mesmo registro. Um negócio pode entrar
   * em mais de um processo ao mesmo tempo (ex.: fluxo padrão + fluxo por regra
   * de valor). Vem preenchido apenas quando a consulta é feita pela entidade.
   */
  processos?: WorkflowInstanceProgress[];
}


interface Params {
  /** Tipo da entidade-raiz (ex.: 'crm_deal') */
  rootEntityType?: string | null;
  /** Id da entidade-raiz */
  rootEntityId?: string | null;
  /** Habilita a consulta (ex.: módulo Workflow ligado) */
  enabled?: boolean;
}

export function useWorkflowInstanceTimeline({ rootEntityType, rootEntityId, enabled = true }: Params) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estado da janela de acompanhamento: timestamp de término (0 = desligada)
  const [janelaAte, setJanelaAte] = useState(0);

  /**
   * Abre a janela quando alguma alteração relevante do registro acontece
   * (produtos alterados, valor, fase...). Quem dispara é quem altera o
   * registro (ex.: useCrmDealItems), via evento DOM com o id no `detail`.
   */
  useEffect(() => {
    if (!rootEntityId) return;

    const aoAlterar = (ev: Event) => {
      const detalhe = (ev as CustomEvent<{ rootEntityId?: string }>).detail;
      if (detalhe?.rootEntityId !== rootEntityId) return;
      setJanelaAte(Date.now() + JANELA_DURACAO_MS);
    };

    window.addEventListener(WORKFLOW_DEAL_CHANGED_EVENT, aoAlterar);
    return () => window.removeEventListener(WORKFLOW_DEAL_CHANGED_EVENT, aoAlterar);
  }, [rootEntityId]);

  // Desliga a janela ao expirar, sem depender de novo evento
  useEffect(() => {
    if (!janelaAte) return;
    const restante = janelaAte - Date.now();
    const timer = window.setTimeout(() => setJanelaAte(0), Math.max(restante, 0));
    return () => window.clearTimeout(timer);
  }, [janelaAte]);

  const janelaAberta = janelaAte > Date.now();

  const query = useQuery({
    // Mesma chave já invalidada pelas mutações/realtime do negócio
    queryKey: ['deal-workflow', rootEntityId, tenantId],
    enabled: !!tenantId && !!rootEntityType && !!rootEntityId && enabled,
    staleTime: 15_000,
    // Janela de acompanhamento: refetch leve por até 60s após alteração; fora dela, sem polling
    refetchInterval: janelaAberta ? JANELA_INTERVALO_MS : false,
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<WorkflowInstanceProgress | null> => {
      const { data, error } = await (supabase.rpc as any)('get_workflow_instance_timeline', {
        p_instance_id: null,
        p_root_entity_type: rootEntityType,
        p_root_entity_id: rootEntityId,
      });

      if (error) {
        logger.error('Erro ao carregar progresso da esteira', { error: error.message });
        throw error;
      }

      const payload = (data ?? null) as WorkflowInstanceProgress | null;
      if (!payload || !payload.instance_id) return null;

      return {
        ...payload,
        etapas: payload.etapas ?? [],
        proximos_passos: payload.proximos_passos ?? [],
        // v1.2.0: normaliza a lista de processos do registro (RPC v1.6.0)
        processos: (payload.processos ?? []).map((p) => ({
          ...p,
          etapas: p.etapas ?? [],
          proximos_passos: p.proximos_passos ?? [],
        })),
      };
    },
  });

  /**
   * Lista de processos do registro. Quando a RPC ainda não devolver a chave
   * (cache antigo), cai para o processo principal — comportamento anterior.
   */
  const processos =
    query.data?.processos?.length ? query.data.processos : query.data ? [query.data] : [];

  return {
    progresso: query.data ?? null,
    /** Todos os processos vivos/encerrados do mesmo registro */
    processos,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

